import type { ComponentContentSchema, SlotGroupDefinition } from '@vibesites/runtime';
import { EditableSlotGroup, RichTextContent } from '@vibesites/runtime/react';
import { useInnerHeroImage } from '../hooks/useInnerHeroImage';
import { usePropertyCountry } from '../utils/propertyCountry';
import {
  INNER_HERO_SUBTITLE_CLASS,
  INNER_HERO_TITLE_CLASS,
  InnerHeroBackground,
  InnerHeroShell,
} from '../ui/InnerHeroShell';

/**
 * Neighborhood page hero. Keeps the `neighborhood-hero.*` keys the shared
 * PageHero instance saved to, so existing edits carry over.
 */

const TITLE = '<p>Neighborhood</p>';
const TITLE_CANADA = '<p>Neighbourhood</p>';
const SUBTITLE =
  '<p>Things to Do Near <span data-type="field-token" data-field="property_name"></span> in <span data-type="field-token" data-field="property_address_city"></span>, <span data-type="field-token" data-field="property_address_state"></span></p>';

const bgImageDef: SlotGroupDefinition = {
  label: 'Background Image',
  fields: {
    image: { label: 'Image', type: 'image', defaultField: 'property_featured_image' },
    alt: { label: 'Alt Text', type: 'text', defaultValue: 'Neighborhood banner' },
  },
  sourceBinding: {
    collection: 'photo_gallery',
    defaultIndex: 0,
    fieldMappings: { image: 'image', alt: 'alt_text' },
  },
  generator: {
    prompt:
      "A photo of the property's surrounding area — a neighborhood view, streetscape, landmark, skyline, or nearby local spot. If the property has none, use an aerial or exterior shot that shows its setting. Alt text describes the chosen photo.",
    context:
      'Full-bleed inner page hero banner behind the "Neighborhood" title, under a dark overlay; crops to a wide landscape strip. Hyperlocal: the image must show this property\'s actual area, not a generic city. Must be sharp, well lit and uncluttered — never a map screenshot, logo, or graphic. Use a photo not already used by the homepage hero or the other inner page heroes (Floor Plans, Amenities, Gallery, Virtual Tour, Contact).',
  },
};

export function NeighborhoodHero() {
  const isCanadian = usePropertyCountry() === 'ca';
  const bg = useInnerHeroImage('neighborhood-hero.bg_image', bgImageDef, 'neighborhood', {
    altText: isCanadian ? 'Neighbourhood banner' : 'Neighborhood banner',
  });

  return (
    <InnerHeroShell
      background={
        <EditableSlotGroup
          groupPath="neighborhood-hero.bg_image"
          definition={bgImageDef}
          className="absolute inset-0"
        >
          <InnerHeroBackground image={bg.image} alt={bg.alt} />
        </EditableSlotGroup>
      }
    >
      <RichTextContent
        path="neighborhood-hero.subtitle"
        label="Subtitle"
        defaultValue={SUBTITLE}
        as="h2"
        className={INNER_HERO_SUBTITLE_CLASS}
      />
      {isCanadian ? (
        <RichTextContent
          path="neighborhood-hero.title_canada"
          label="Title (Canadian Properties)"
          defaultValue={TITLE_CANADA}
          as="h1"
          className={INNER_HERO_TITLE_CLASS}
        />
      ) : (
        <RichTextContent
          path="neighborhood-hero.title"
          label="Title"
          defaultValue={TITLE}
          as="h1"
          className={INNER_HERO_TITLE_CLASS}
        />
      )}
    </InnerHeroShell>
  );
}

export const contentSchema: ComponentContentSchema = {
  slot: 'NeighborhoodHero',
  displayName: 'Neighborhood Hero',
  tokens: {
    'neighborhood-hero.subtitle': {
      label: 'Subtitle',
      type: 'richtext',
      defaultValue:
        '<p>Things to Do Near <span data-type="field-token" data-field="property_name"></span> in <span data-type="field-token" data-field="property_address_city"></span>, <span data-type="field-token" data-field="property_address_state"></span></p>',
    },
    'neighborhood-hero.title': {
      label: 'Title',
      type: 'richtext',
      defaultValue: '<p>Neighborhood</p>',
    },
    'neighborhood-hero.title_canada': {
      label: 'Title (Canadian Properties)',
      type: 'richtext',
      defaultValue: '<p>Neighbourhood</p>',
    },
  },
  slotGroups: {
    'neighborhood-hero.bg_image': bgImageDef,
  },
};

(NeighborhoodHero as unknown as { contentSchema: ComponentContentSchema }).contentSchema = contentSchema;

export default NeighborhoodHero;
